import { useUserStore } from '@/stores/userStore';
import { getMeQueryOptions } from '@/composables/useMeQuery';
import { getMeV3QueryOptions } from '@/composables/useMeV3Query';
import { queryClient } from '@/plugins/vueQuery';
import { updateLDContext } from '@/plugins/launchdarkly';
import { getMeQueryKey, getMeV3QueryKey } from './queries/users';
import { isGranularPermissionsEnabled, pinGranularPermissionsFlag, resetGranularPermissionsFlag } from './granularPermissionsFlag';

/** Bounds the flag-on wait. Axios has no request timeout, so a hung v3 /me would never settle. */
export const V3_ME_BOOTSTRAP_TIMEOUT_MS = 5_000;

let bootstrapPromise: Promise<void> | null = null;

function logV3MeBootstrapFailure(error: unknown): void {
  console.warn('[permissions] v3 /me failed during session bootstrap', error);
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('v3 /me timed out during session bootstrap'));
    }, timeoutMs);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

async function performBootstrap(isCurrent: () => boolean): Promise<void> {
  const userStore = useUserStore();
  let v3FailureLogged = false;
  const logV3FailureOnce = (error: unknown) => {
    if (v3FailureLogged) return;
    v3FailureLogged = true;
    logV3MeBootstrapFailure(error);
  };

  // Started immediately so the cache is warm when the store mounts. Not awaited
  // until after the flag pin, and only when the flag is on — flag-off login must
  // not wait on a slow or hung v3 /me. A rejection and a timeout share one warning.
  const v3Me = queryClient.query(getMeV3QueryOptions()).catch(logV3FailureOnce);

  const me = await queryClient.query(getMeQueryOptions());

  userStore.setPermissions(me.permissions);
  userStore.setIsSuperAdmin(me.user.isSuperAdmin);

  try {
    const ldClient = await updateLDContext();
    if (ldClient) {
      await ldClient.waitForInitialization(5);
    }
  } catch (error) {
    // /me succeeded — an LD outage or ad blocker must not brick authenticated navigation.
    console.warn('[LaunchDarkly] Failed to identify/initialize during session bootstrap', error);
  }

  // Skip pinning if resetSessionBootstrap (or a failed-bootstrap memo clear) replaced
  // this promise while we were awaiting LD — restoring the pin would undo that clear.
  if (!isCurrent()) {
    return;
  }

  // Pinned after the LD identify attempt settles (success or failure) so it reads
  // whatever context is now in place, and is frozen for the rest of the session —
  // see granularPermissionsFlag.ts for why this one flag is pinned rather than read live.
  pinGranularPermissionsFlag();

  if (isGranularPermissionsEnabled()) {
    await withTimeout(v3Me, V3_ME_BOOTSTRAP_TIMEOUT_MS).catch(logV3FailureOnce);
  }
}

/**
 * Bootstraps the session once per app load: fetches /me, hydrates permissions
 * and isSuperAdmin, identifies the LaunchDarkly client with the resulting
 * context, and waits for it to be ready. The promise is memoized so
 * concurrent and subsequent navigations share a single in-flight or
 * completed bootstrap rather than repeating the work.
 *
 * On /me failure the memo is cleared so the next call retries from scratch, and
 * the error is rethrown for the caller (the router guard) to handle — this
 * intentionally does not catch/log /me failures internally, so a failed
 * bootstrap can block navigation instead of silently continuing.
 *
 * LaunchDarkly identify/initialization failures are non-fatal: they are
 * logged and bootstrap still resolves, leaving flag reads on defaults.
 *
 * v3 /me is started in parallel and awaited only after the granular-permissions
 * flag is pinned, and only when that flag is on. A v3 rejection or timeout is
 * logged once and does not fail bootstrap. Flag-off sessions do not wait for it.
 */
export function bootstrapSession(): Promise<void> {
  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  // `running` is both the memoized identity and the promise callers await, so
  // performBootstrap can tell whether it is still the current session bootstrap
  // after awaiting LD (resetSessionBootstrap nulls the memo).
  const running: Promise<void> = performBootstrap((): boolean => bootstrapPromise === running).catch((error: unknown) => {
    if (bootstrapPromise === running) {
      bootstrapPromise = null;
    }
    throw error;
  });
  bootstrapPromise = running;
  return running;
}

/**
 * Clears the memoized bootstrap and the cached /me query so the next
 * navigation re-fetches identity. Call from logout / local-state reset so a
 * same-tab re-login cannot reuse the previous user's permissions or LD context.
 * Nulling the memo also invalidates any in-flight bootstrap so it will not
 * re-pin granular permissions after the clear.
 */
export function resetSessionBootstrap(): void {
  bootstrapPromise = null;
  queryClient.removeQueries({ queryKey: getMeQueryKey() });
  queryClient.removeQueries({ queryKey: getMeV3QueryKey() });
  resetGranularPermissionsFlag();
}
